import { CloseRounded } from '@mui/icons-material';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { SignerTypeCard } from './SignerTypeCard';
import { SignerType } from './types';

const customStyles: Record<string, SxProps<Theme>> = {
  drawerBox: {
    flexDirection: 'column',
    display: 'flex',
    height: '100%',
    overflowY: 'auto',
    width: '440px',
  },
  contentBox: {
    padding: '16px',
    flex: 1,
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    maxWidth: '80%',
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '32px',
    marginTop: '16px',
    marginLeft: '4px',
  },
  closeButton: {
    color: '#666',
  },
  description: {
    color: (theme) => theme.palette.text.secondary,
    marginTop: '48px',
    textAlign: 'center',
    marginBottom: '24px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginTop: '16px',
    marginLeft: '16px',
    marginRight: '16px',
  },
};

interface SignerTypeDrawerContentSelectorProps {
  onClose: () => void;
  onSignerTypeClick: (signerType: SignerType) => void;
  fixedSignerCount?: number;
  dynamicSignerCount?: number;
}

interface SignerTypeCard {
  type: SignerType;
  title: string;
  description: string;
  imageUrl: string;
}

export function SignerTypeDrawerContentSelector({
  onClose,
  onSignerTypeClick,
  fixedSignerCount = 0,
  dynamicSignerCount = 0,
}: SignerTypeDrawerContentSelectorProps) {
  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();

  const isDynamicSignatureEnabled = checkFlag('DynamicSignature');

  const signerTypeCards: SignerTypeCard[] = [
    {
      type: SignerType.FIXED,
      title: lang.formSignatureBuilder.fixedSigner,
      description: lang.formSignatureBuilder.fixedSignerDescription,
      imageUrl: 'https://data-scope.s3.us-west-2.amazonaws.com/assets/fixed_signer.png',
    },
    {
      type: SignerType.DYNAMIC,
      title: lang.formSignatureBuilder.dynamicSigner,
      description: lang.formSignatureBuilder.dynamicSignerCardDescription,
      imageUrl: 'https://data-scope.s3.us-west-2.amazonaws.com/assets/dynamic_signer.png',
    },
  ];

  const handleCardClick = (type: SignerType) => {
    if (type === SignerType.DYNAMIC && !isDynamicSignatureEnabled) {
      return;
    }

    onSignerTypeClick(type);
  };

  return (
    <Box sx={customStyles.drawerBox}>
      <Box sx={customStyles.contentBox}>
        <Box sx={customStyles.headerBox}>
          <Typography sx={customStyles.title}>
            {lang.formSignatureBuilder.addSignersTitle}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={customStyles.closeButton}
          >
            <CloseRounded />
          </IconButton>
        </Box>

        <Typography sx={customStyles.description}>
          {lang.formSignatureBuilder.selectConfigurationType}
        </Typography>

        <Box sx={customStyles.cardContainer}>
          {signerTypeCards.map((card) => (
            <SignerTypeCard
              key={card.type}
              type={card.type}
              title={card.title}
              description={card.description}
              imageUrl={card.imageUrl}
              selectedCount={card.type === SignerType.FIXED ? fixedSignerCount : dynamicSignerCount}
              onClick={() => handleCardClick(card.type)}
              disabled={card.type === SignerType.DYNAMIC && !isDynamicSignatureEnabled}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default SignerTypeDrawerContentSelector;
